import {FC, useContext, useEffect, useRef, useState} from "react";
import Card from "../components/ui/Card";
import {SubmitHandler, useForm} from "react-hook-form";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import Textarea from "../components/ui/Textarea";
import useApi from "../hooks/useApi";
import Autocomplete from "../components/ui/Autocomplete";
import Button from "../components/ui/Button";
import {BiPlusCircle} from "react-icons/bi";
import {toast} from "react-hot-toast";
import {GroupType} from "../types/ApiTypes";
import {useAccount} from "@azure/msal-react";
import axios from "axios";
import Heading from "../components/ui/Heading";
import {AppContext} from "../context/AppContext";
import {GroupsContext} from "../context/GroupsContext";
import {motion} from 'framer-motion'
import {useNavigate} from "react-router-dom";
import handleToastError from "../helpers/handleToastError";

interface Inputs {
    name: string,
    beschreibung: string,
}

interface IGroupType {
    value: GroupType;
    label: string;
}

interface IMutatedUnit {
    label: string,
    value: IMutatedUnitValue
}

interface IMutatedUnitValue {
    id: number,
    children:number[],
    owner:string
}

const groupTypeOptions: IGroupType[] = [
    {value: 20, label:'Workspace'},
    {value: 25, label:'Projekt'},
];

const CreateIntern: FC = () => {
    const mounted = useRef(false);
    const navigate = useNavigate();
    const {register, watch, handleSubmit, formState:{errors}} = useForm<Inputs>();
    const [customErrors, setCustomErrors] = useState({
        bereich: '',
        aufgabengebiet: '',
        stellvertreter: '',
    });
    const {fetchCompanyUnits, fetchCompanyEmployees, createGroupRequest} = useApi();
    const account = useAccount();
    const appCtx = useContext(AppContext);
    const groupCtx = useContext(GroupsContext);

    const [mainUnitLoading, setMainUnitLoading] = useState(true);
    const [secUnitLoading, setSecUnitLoading] = useState(false);

    const [units,setUnits] = useState<{ main: IMutatedUnit[], secondary: IMutatedUnit[]}>();
    const [secondaryUnits,setSecondaryUnits] = useState<IMutatedUnit[]>([]);
    const [employees, setEmployees] = useState<{label: string, value: string}[]>([]);

    const [selectedGroupType, setSelectedGroupType] = useState<IGroupType>(groupTypeOptions[0]);
    const [selectedBereich, setSelectedBereich] = useState<IMutatedUnit>();
    const [selectedGebiet, setSelectedGebiet] = useState<IMutatedUnit>();
    const [selectedEmployee, setSelectedEmployee] = useState<{ label: string, value: string}>();

    useEffect(() => {
        mounted.current = true;
        return () => {mounted.current=false}
    },[]);

    useEffect(() => {
        setMainUnitLoading(true);
        fetchCompanyUnits().then(response => {
            if(!response || !mounted.current) return;
            console.log(response)
            const main = response.main.map(x => ({label: x.name, value: {id: x.id, children:response.secondary.filter(y => y.parent_unit!.id === x.id).map(y => y.id), owner: x.owner}}));
            const secondary = response.secondary.map(x => ({label: x.name, value: {id: x.id, children:response.secondary.filter(y => y.parent_unit!.id === x.id).map(y => y.id), owner: x.owner}}));
            setUnits({main,secondary});
            setMainUnitLoading(false);
        });
        if(appCtx.state.user) {
            setEmployees(appCtx.state.user.map(x => ({label:x.displayName, value:x.id})))
        }
    },[fetchCompanyUnits, fetchCompanyEmployees, appCtx.state.user]);

    const onGroupTypeChange = (type: IGroupType) => {
        setSelectedGroupType(type);
    }

    const onMainUnitChange = (type: IMutatedUnit) => {
        setSelectedBereich(type);
        setSelectedGebiet(undefined);
        setSecondaryUnits([]);
        setSecUnitLoading(true);
        if(units) setSecondaryUnits(units.secondary.filter(x => type.value.children.includes(x.value.id)));
        setSecUnitLoading(false);
    }

    const onSecUnitChange = (type: IMutatedUnit ) => {
        setSelectedGebiet(type);
    }

    const onEmployeeSelected = (type: { label: string, value: string}) => {
        setSelectedEmployee(type);
    }

    const onSubmit: SubmitHandler<Inputs> = async (e) => {
        let errors=0;
        if(!selectedBereich) {
            setCustomErrors((prev) => ({...prev,bereich: 'Kein Bereich wurde ausgewählt'}))
            errors++;
        }
        if(!selectedEmployee) {
            setCustomErrors((prev) => ({...prev,stellvertreter: 'Kein Stellvertreter wurde ausgewählt'}))
            errors++;
        }
        if(errors) return;
        await toast.promise(createGroupRequest({
            type: selectedGroupType.value,
            name: e.name,
            unit: selectedBereich!.value.id,
            description: e.beschreibung,
            childUnit: selectedGebiet?.value.id,
            primary_owner: selectedGebiet ? selectedGebiet.value.owner : selectedBereich!.value.owner,
            secondary_owner: selectedEmployee!.value,
            requester: account!.localAccountId
        }),{
            loading: 'Der Formular wird abgeschickt',
            success: (res) => {
                groupCtx.dispatch({type: 'REPLACE_GROUP', payload: res});
                setCustomErrors({bereich: '',stellvertreter: '',aufgabengebiet: ''});
                navigate(`/space/${res.requestId}`);
                return <span className='text-sm'>Der Antrag für den Bereich<br/><b>{selectedGroupType.value} {watch('name')}</b> wurde erstellt.</span>
            },
            error: (err) => handleToastError(err, `Der Antrag für den Bereich ${selectedGroupType.value} ${watch('name')} konnte nicht erstellt werden`)
        });
    }

    return (
        <motion.section initial={{
            opacity: 0
        }} exit={{
            opacity: 0
        }} animate={{
            opacity: 1
        }}>
            <Heading>Erstelle einen internen Arbeitsbereich</Heading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col space-y-4">
                    <Card>
                        <form autoComplete='off' className='space-y-4' onSubmit={handleSubmit(onSubmit)}>
                            <div>
                                <label className='font-semibold'>Spacetyp</label>
                                <Select options={groupTypeOptions} defaultValue={groupTypeOptions[0]} onChange={onGroupTypeChange}/>
                            </div>
                            <div>
                                <label className='font-semibold'>Bereich</label>
                                <Select<IMutatedUnitValue> loading={mainUnitLoading} options={units?.main ? units.main : []} onChange={onMainUnitChange} placeholder='Wähle den Bereich'/>
                                {customErrors.bereich && <span className='text-xs text-error-brighter'>{customErrors.bereich}</span>}
                            </div>
                            {secondaryUnits.length > 0 && (
                                <div>
                                    <label className='font-semibold'>Aufgabengebiet</label>
                                    <Select<IMutatedUnitValue> loading={secUnitLoading} options={secondaryUnits} onChange={onSecUnitChange} placeholder='Wähle den Aufgabengebiet'/>
                                </div>
                            )}
                            <div>
                                <label className='font-semibold' htmlFor='name'>Name</label>
                                <Input type="text" label='Name' id='name' className='block' placeholder='Name' {...register('name', {required: true, minLength: 2})}/>
                                {errors.name && <span className='text-xs text-error-brighter'>Name ist ein Pflichtfeld und muss mindestens 2 Zeichen lang sein.</span>}
                            </div>
                            <div>
                                <label className='font-semibold' htmlFor='beschreibung'>Beschreibung</label>
                                <Textarea label='Beschreibung' id='beschreibung' className='block' placeholder='Beschreibung' {...register('beschreibung', {required: true, minLength: 30})}/>
                                {errors.beschreibung && <span className='text-xs text-error-brighter'>Beschreibung ist ein Pflichtfeld und muss mindestens 30 Zeichen lang sein.</span>}
                            </div>
                            <div>
                                <label className='font-semibold' htmlFor='Stellvertreter'>Stellvertreter</label>
                                <Autocomplete<string> options={employees} onChange={onEmployeeSelected}/>
                                {customErrors.stellvertreter && <span className='text-xs text-error-brighter'>{customErrors.stellvertreter}</span>}
                            </div>
                            <Button type='submit'><BiPlusCircle className='mr-1'/> Erstellen</Button>
                        </form>
                    </Card>
                </div>
                <div className="flex flex-col space-y-4">
                    <Card title={`${selectedGroupType.value} ${watch('name') ? watch('name') : 'Name des Arbeitsbereiches'}`}>
                        <div className='space-y-4'>
                            <span className='italic'>{watch('beschreibung')}</span>
                            <ul>
                                {selectedBereich && <li>Bereich: {selectedBereich.label}</li>}
                                {selectedGebiet && <li>Aufgabengebiet: {selectedGebiet.label}</li>}
                                {(selectedBereich && !selectedGebiet) &&<li>Besitzer: {employees.find(x => x.value === selectedBereich?.value.owner)?.label}</li>}
                                {selectedGebiet &&<li>Besitzer: {employees.find(x => x.value === selectedGebiet?.value.owner)?.label}</li>}
                                {selectedEmployee && <li>Stellvertreter: {selectedEmployee.label}</li>}
                            </ul>
                        </div>
                    </Card>
                </div>
            </div>
        </motion.section>
    );
};

export default CreateIntern;